import { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import { ImportExportOutlined as ImportIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useImportCreditApp } from '@src/tools/anonymizer/api/credit-app-import-api'
import { TARGET_ENVIRONMENTS } from '@src/tools/anonymizer/types/Constants'
import { useToast } from '@src/shared/hooks/useToast'

const ImportCreditAppPage = () => {
  const { t } = useTranslation()
  const [targetEnv, setTargetEnv] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [mutate, isPending, isError, reset, data, error] = useImportCreditApp()
  const { showSuccess, showError, ToastComponent } = useToast()

  const handleImport = () => {
    reset()
    mutate(
      { referenceNumber: Number(referenceNumber), targetEnv },
      {
        onSuccess: (result) => {
          const total = result.results.reduce((sum, r) => sum + r.importedCount, 0)
          showSuccess(t('anonymizer.import.success', { count: total, env: targetEnv }))
        },
        onError: () => {
          showError(t('anonymizer.import.error'))
        },
      },
    )
  }

  const parsedRef = Number(referenceNumber)
  const isValidRef = Number.isInteger(parsedRef) && parsedRef >= 100000
  const canSubmit = targetEnv && isValidRef && !isPending

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" gutterBottom>
        {t('anonymizer.import.title')}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>{t('anonymizer.import.targetEnv')}</InputLabel>
            <Select
              value={targetEnv}
              label={t('anonymizer.import.targetEnv')}
              onChange={(e) => setTargetEnv(e.target.value)}
            >
              {TARGET_ENVIRONMENTS.map((env) => (
                <MenuItem key={env} value={env}>
                  {env.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label={t('anonymizer.import.referenceNumber')}
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            sx={{ flexGrow: 1 }}
          />

          <Button
            variant="contained"
            startIcon={isPending ? <CircularProgress size={18} color="inherit" /> : <ImportIcon />}
            onClick={handleImport}
            disabled={!canSubmit}
          >
            {isPending ? t('anonymizer.import.importing') : t('anonymizer.import.importButton')}
          </Button>
        </Box>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error?.response?.data as Record<string, unknown>)?.error as string || t('anonymizer.import.error')}
        </Alert>
      )}

      {data && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('anonymizer.import.columns.database')}</TableCell>
                <TableCell>{t('anonymizer.import.columns.container')}</TableCell>
                <TableCell align="right">{t('anonymizer.import.columns.deleted')}</TableCell>
                <TableCell align="right">{t('anonymizer.import.columns.imported')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.results.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.databaseName}</TableCell>
                  <TableCell>{row.containerName}</TableCell>
                  <TableCell align="right">{row.deletedCount}</TableCell>
                  <TableCell align="right">{row.importedCount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ToastComponent />
    </Container>
  )
}

export default ImportCreditAppPage
